import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useUser, useClerk } from '@clerk/react';
import { usersApi, availabilityApi } from '@/api';
import { useInspectorProfile } from '@/contexts/InspectorProfileContext';
import { AgreementMarkdown } from '@/components/AgreementMarkdown';
import { User, InspectorAvailabilityDay } from '@/types';
import { Skeleton } from '@/ui';
import { BookingPageCard } from './Settings/BookingPageCard';
import { BrandingCard } from './Settings/BrandingCard';
import {
  DEFAULT_AVAILABILITY,
  DEFAULT_BRAND_COLOR,
  ENTITY_TYPE_OPTIONS,
  HEX_RE,
  detectBrowserTimezone,
} from './Settings/_constants';

type TabId = 'profile' | 'brand' | 'booking' | 'agreement' | 'payments' | 'communications' | 'account';

const TABS: { id: TabId; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'brand', label: 'Brand' },
  { id: 'booking', label: 'Booking' },
  { id: 'agreement', label: 'Agreement' },
  { id: 'payments', label: 'Payments' },
  { id: 'communications', label: 'Communications' },
  { id: 'account', label: 'Account' },
];

interface ProfileFormData {
  firstName: string;
  lastName: string;
  phone: string;
  companyName: string;
  licenseNumber: string;
  serviceArea: string;
  brandColor: string;
  signatureText: string;
  businessEntityType: string;
  businessLocation: string;
  agreementTemplate: string;
  slug: string;
  timezone: string;
  defaultDuration: number;
  reviewLink: string;
  reviewRequestEnabled: boolean;
  requirePaymentBeforeDelivery: boolean;
  paymentInstructions: string;
  paymentMethodUrl: string;
  paymentMethodLabel: string;
}

function userToForm(user: User | null): ProfileFormData {
  return {
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    phone: user?.phone ?? '',
    companyName: user?.companyName ?? '',
    licenseNumber: user?.licenseNumber ?? '',
    serviceArea: user?.serviceArea ?? '',
    brandColor: user?.brandColor ?? DEFAULT_BRAND_COLOR,
    signatureText: user?.signatureText ?? '',
    businessEntityType: user?.businessEntityType ?? '',
    businessLocation: user?.businessLocation ?? '',
    agreementTemplate: user?.agreementTemplate ?? '',
    slug: user?.slug ?? '',
    // The schema defaults timezone to 'America/Los_Angeles' for every new
    // user, which then takes precedence over browser detection through ||.
    // Treat the system default as "unset" so we fall through to the
    // browser-detected zone — the inspector can still pick LA if that's
    // actually their zone, the change just stops every non-LA inspector
    // from seeing LA on first open.
    timezone:
      user?.timezone && user.timezone !== 'America/Los_Angeles'
        ? user.timezone
        : detectBrowserTimezone(),
    defaultDuration: user?.defaultDuration ?? 120,
    reviewLink: user?.reviewLink ?? '',
    reviewRequestEnabled: user?.reviewRequestEnabled ?? true,
    requirePaymentBeforeDelivery: user?.requirePaymentBeforeDelivery ?? true,
    paymentInstructions: user?.paymentInstructions ?? '',
    paymentMethodUrl: user?.paymentMethodUrl ?? '',
    paymentMethodLabel: user?.paymentMethodLabel ?? '',
  };
}

// Converts a free-form company name into a valid booking slug.
function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

function extractBackendError(err: unknown): string | null {
  if (
    typeof err === 'object' && err !== null && 'response' in err &&
    typeof (err as { response?: { data?: { error?: unknown } } }).response?.data?.error === 'string'
  ) {
    return (err as { response: { data: { error: string } } }).response.data.error;
  }
  return null;
}

const Settings: React.FC = () => {
  const { user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const { setProfile: setSharedProfile } = useInspectorProfile();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const headshotInputRef = useRef<HTMLInputElement | null>(null);

  const [activeTab, setActiveTab] = useState<TabId>('profile');
  const [profile, setProfile] = useState<User | null>(null);
  const [formData, setFormData] = useState<ProfileFormData>(userToForm(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resettingColor, setResettingColor] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const [availabilityDays, setAvailabilityDays] = useState<InspectorAvailabilityDay[]>(DEFAULT_AVAILABILITY);
  const [blockedDates, setBlockedDates] = useState<{ blockedDate: string; reason: string | null }[]>([]);
  const [newBlockedDate, setNewBlockedDate] = useState('');
  const [newBlockedReason, setNewBlockedReason] = useState('');
  const [agreementPreview, setAgreementPreview] = useState(false);

  // true while the slug field has not been manually edited by the user — allows
  // it to be driven by the company name field automatically.
  const [slugAutoFilled, setSlugAutoFilled] = useState(true);
  const [slugCheck, setSlugCheck] = useState<{
    status: 'idle' | 'checking' | 'available' | 'taken';
    suggestion: string | null;
  }>({ status: 'idle', suggestion: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [profileResp, availabilityResp] = await Promise.all([
          usersApi.getProfile(),
          availabilityApi.get(),
        ]);
        if (cancelled) return;
        if (profileResp.success && profileResp.data) {
          setProfile(profileResp.data);
          setFormData(userToForm(profileResp.data));
          // If the inspector already has a slug, don't auto-overwrite it.
          if (profileResp.data.slug) setSlugAutoFilled(false);
        }
        if (availabilityResp.success && availabilityResp.data) {
          const saved = availabilityResp.data.days;
          const merged = saved.length === 0
            ? DEFAULT_AVAILABILITY
            : DEFAULT_AVAILABILITY.map((d) => {
                const match = saved.find((s) => s.dayOfWeek === d.dayOfWeek);
                return match ?? { ...d, isActive: false };
              });
          setAvailabilityDays(merged);
          setBlockedDates(availabilityResp.data.blockedDates);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load profile', err);
          setMessage({ kind: 'error', text: 'Failed to load your profile.' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (name === 'slug') {
      // Clearing the slug re-enables auto-fill so the next company name change picks up.
      // Typing a non-empty value locks the slug to what the user chose.
      setSlugAutoFilled(value === '');
      setSlugCheck({ status: 'idle', suggestion: null });
      setFormData(prev => ({ ...prev, slug: value }));
    } else if (name === 'companyName' && slugAutoFilled) {
      // Drive the slug from the company name while user hasn't manually edited it.
      const generated = slugify(value);
      setSlugCheck({ status: 'idle', suggestion: null });
      setFormData(prev => ({ ...prev, companyName: value, slug: generated }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    setMessage(null);
  };

  const handleSlugSuggestionAccept = useCallback((suggestion: string) => {
    setSlugAutoFilled(false);
    setSlugCheck({ status: 'idle', suggestion: null });
    setFormData(prev => ({ ...prev, slug: suggestion }));
  }, []);

  // Debounced slug availability check — fires 500 ms after the slug settles.
  useEffect(() => {
    const slug = formData.slug.trim();
    // Only check slugs that look valid enough to bother hitting the server.
    if (slug.length < 3) {
      setSlugCheck({ status: 'idle', suggestion: null });
      return;
    }
    setSlugCheck(prev => ({ ...prev, status: 'checking' }));
    const timer = setTimeout(async () => {
      try {
        const resp = await usersApi.checkSlug(slug);
        if (resp.success && resp.data) {
          setSlugCheck({
            status: resp.data.available ? 'available' : 'taken',
            suggestion: resp.data.suggestion ?? null,
          });
        } else {
          setSlugCheck({ status: 'idle', suggestion: null });
        }
      } catch {
        setSlugCheck({ status: 'idle', suggestion: null });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.slug]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    const brandColor = HEX_RE.test(formData.brandColor) ? formData.brandColor : DEFAULT_BRAND_COLOR;
    try {
      const response = await usersApi.updateProfile({ ...formData, brandColor });
      if (response.success && response.data) {
        setProfile(response.data);
        setSharedProfile(response.data);
        setFormData(userToForm(response.data));
        setMessage({ kind: 'success', text: 'Saved.' });
      } else {
        setMessage({ kind: 'error', text: 'Failed to save settings.' });
      }
    } catch (err) {
      setMessage({ kind: 'error', text: extractBackendError(err) || 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  // Booking tab saves profile fields (slug, timezone) AND availability in one action.
  const handleSaveBooking = async () => {
    setSaving(true);
    setMessage(null);
    const brandColor = HEX_RE.test(formData.brandColor) ? formData.brandColor : DEFAULT_BRAND_COLOR;
    try {
      const [profileResp] = await Promise.all([
        usersApi.updateProfile({ ...formData, brandColor }),
        availabilityApi.put({
          days: availabilityDays.filter((d) => d.isActive),
          blockedDates,
        }),
      ]);
      if (profileResp.success && profileResp.data) {
        setProfile(profileResp.data);
        setSharedProfile(profileResp.data);
        setFormData(userToForm(profileResp.data));
      }
      setMessage({ kind: 'success', text: 'Booking settings saved.' });
    } catch (err) {
      setMessage({ kind: 'error', text: extractBackendError(err) || 'Failed to save booking settings.' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddBlockedDate = () => {
    if (!newBlockedDate) return;
    if (blockedDates.some((b) => b.blockedDate === newBlockedDate)) return;
    setBlockedDates((prev) =>
      [...prev, { blockedDate: newBlockedDate, reason: newBlockedReason.trim() || null }].sort(
        (a, b) => a.blockedDate.localeCompare(b.blockedDate)
      )
    );
    setNewBlockedDate('');
    setNewBlockedReason('');
  };

  const handleRemoveBlockedDate = (date: string) => {
    setBlockedDates((prev) => prev.filter((b) => b.blockedDate !== date));
  };

  const handleLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    setUploadingLogo(true);
    setMessage(null);
    try {
      const updated = await usersApi.uploadLogo(file);
      setProfile(updated);
      setSharedProfile(updated);
      setMessage({ kind: 'success', text: 'Logo uploaded.' });
    } catch {
      setMessage({ kind: 'error', text: 'Failed to upload logo. Try a PNG, JPG, WebP, or SVG under 5 MB.' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleResetBrandColor = async () => {
    setResettingColor(true);
    setMessage(null);
    try {
      const response = await usersApi.updateProfile({ ...formData, brandColor: DEFAULT_BRAND_COLOR });
      if (response.success && response.data) {
        setProfile(response.data);
        setSharedProfile(response.data);
        setFormData(userToForm(response.data));
        setMessage({ kind: 'success', text: 'Brand color reset to default.' });
      } else {
        setMessage({ kind: 'error', text: 'Failed to reset brand color.' });
      }
    } catch {
      setMessage({ kind: 'error', text: 'Failed to reset brand color. Please try again.' });
    } finally {
      setResettingColor(false);
    }
  };

  const handleRemoveLogo = async () => {
    setUploadingLogo(true);
    setMessage(null);
    try {
      const response = await usersApi.deleteLogo();
      if (response.success && response.data) {
        setProfile(response.data);
        setSharedProfile(response.data);
        setMessage({ kind: 'success', text: 'Logo removed.' });
      }
    } catch {
      setMessage({ kind: 'error', text: 'Failed to remove logo.' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleHeadshotFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (headshotInputRef.current) headshotInputRef.current.value = '';
    if (!file) return;
    setUploadingHeadshot(true);
    setMessage(null);
    try {
      const updated = await usersApi.uploadHeadshot(file);
      setProfile(updated);
      setSharedProfile(updated);
      setMessage({ kind: 'success', text: 'Headshot uploaded.' });
    } catch {
      setMessage({ kind: 'error', text: 'Failed to upload headshot. Try a PNG, JPG, or WebP under 5 MB.' });
    } finally {
      setUploadingHeadshot(false);
    }
  };

  const handleRemoveHeadshot = async () => {
    setUploadingHeadshot(true);
    setMessage(null);
    try {
      const response = await usersApi.deleteHeadshot();
      if (response.success && response.data) {
        setProfile(response.data);
        setSharedProfile(response.data);
        setMessage({ kind: 'success', text: 'Headshot removed.' });
      }
    } catch {
      setMessage({ kind: 'error', text: 'Failed to remove headshot.' });
    } finally {
      setUploadingHeadshot(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <h1 className="font-display text-display-sm text-ink mb-8">Settings</h1>
        <div className="space-y-4">
          <div className="card space-y-4">
            <Skeleton width="w-1/4" height="h-5" />
            <div className="grid grid-cols-2 gap-4">
              <Skeleton height="h-10" />
              <Skeleton height="h-10" />
            </div>
            <Skeleton height="h-10" />
          </div>
          <div className="card space-y-4">
            <Skeleton width="w-1/3" height="h-5" />
            <Skeleton height="h-10" />
            <div className="grid grid-cols-2 gap-4">
              <Skeleton height="h-10" />
              <Skeleton height="h-10" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const saveFooter = (onSave: () => void) => (
    <div className="space-y-3 pt-2">
      {message && (
        <div className={`p-3 rounded-md text-body-sm ${
          message.kind === 'success'
            ? 'bg-sev-maintenance-soft text-sev-maintenance-ink border border-sev-maintenance/20'
            : 'bg-sev-major-soft text-sev-major-ink border border-sev-major/20'
        }`}>
          {message.text}
        </div>
      )}
      <div className="flex justify-end">
        <button onClick={onSave} className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="font-display text-display-sm text-ink mb-6">Settings</h1>

      {/* Tab bar */}
      <div className="flex border-b border-border-subtle mb-8 flex-wrap">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setMessage(null); }}
            className={`px-4 py-2.5 text-body-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-brand text-brand'
                : 'border-transparent text-ink-muted hover:text-ink'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Profile tab */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-heading-md text-ink mb-4">Personal Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-body-sm font-medium text-ink-muted mb-1">First Name</label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="input-field" />
              </div>
              <div>
                <label className="block text-body-sm font-medium text-ink-muted mb-1">Last Name</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="input-field" />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-body-sm font-medium text-ink-muted mb-1">Phone</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="(555) 123-4567" className="input-field" />
            </div>
            <div className="mt-4">
              <label className="block text-body-sm font-medium text-ink-muted mb-2">Headshot</label>
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 rounded-full border border-border bg-surface-2 overflow-hidden flex items-center justify-center">
                  {profile?.headshotUrl ? (
                    <img
                      src={profile.headshotUrl}
                      alt="Headshot preview"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-caption text-ink-faint">No photo</span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={headshotInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleHeadshotFile}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => headshotInputRef.current?.click()}
                    disabled={uploadingHeadshot}
                    className="btn-secondary"
                  >
                    {uploadingHeadshot ? 'Uploading…' : profile?.headshotUrl ? 'Replace Photo' : 'Upload Photo'}
                  </button>
                  {profile?.headshotUrl && (
                    <button
                      type="button"
                      onClick={handleRemoveHeadshot}
                      disabled={uploadingHeadshot}
                      className="px-4 py-2 text-body-sm font-medium rounded-md border border-sev-major/30 bg-surface-1 text-sev-major hover:bg-sev-major-soft disabled:opacity-50 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-2 text-caption text-ink-subtle">PNG, JPG, or WebP. Up to 5 MB. Shown on your public profile at /inspector/your-slug.</p>
            </div>
          </div>

          <div className="card">
            <h2 className="text-heading-md text-ink mb-4">Company Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-body-sm font-medium text-ink-muted mb-1">Company Name</label>
                <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} placeholder="Your Company Name" className="input-field" />
                <p className="mt-1 text-caption text-ink-subtle">Used on report covers and footers in place of Flowspect branding.</p>
              </div>
              <div>
                <label className="block text-body-sm font-medium text-ink-muted mb-1">License Number</label>
                <input type="text" name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} placeholder="License #" className="input-field" />
              </div>
              <div>
                <label className="block text-body-sm font-medium text-ink-muted mb-1">Service Area</label>
                <input
                  type="text"
                  name="serviceArea"
                  value={formData.serviceArea}
                  onChange={handleChange}
                  placeholder="Greater Seattle & Eastside"
                  maxLength={200}
                  className="input-field"
                />
                <p className="mt-1 text-caption text-ink-subtle">Free-text description shown on your public /inspector/:slug profile.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-body-sm font-medium text-ink-muted mb-1">Entity Type</label>
                  <select name="businessEntityType" value={formData.businessEntityType} onChange={handleChange} className="input-field">
                    {ENTITY_TYPE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt || 'Select…'}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-body-sm font-medium text-ink-muted mb-1">Location</label>
                  <input type="text" name="businessLocation" value={formData.businessLocation} onChange={handleChange} placeholder="Austin, TX" className="input-field" />
                </div>
              </div>
              <p className="text-caption text-ink-subtle">Shown under your company name in the sidebar — e.g., &ldquo;Sole proprietor &middot; Austin, TX&rdquo;.</p>
            </div>
          </div>

          {saveFooter(handleSave)}
        </div>
      )}

      {/* Brand tab */}
      {activeTab === 'brand' && (
        <div className="space-y-6">
          <BrandingCard
            profile={profile}
            brandColor={formData.brandColor}
            companyName={formData.companyName}
            fileInputRef={fileInputRef}
            uploadingLogo={uploadingLogo}
            resettingColor={resettingColor}
            onChange={handleChange}
            onLogoFile={handleLogoFile}
            onRemoveLogo={handleRemoveLogo}
            onResetBrandColor={handleResetBrandColor}
          />
          {saveFooter(handleSave)}
        </div>
      )}

      {/* Booking tab */}
      {activeTab === 'booking' && (
        <div className="space-y-6">
          {/* Default duration */}
          <div className="card">
            <h2 className="text-heading-md text-ink mb-1">Default Inspection Duration</h2>
            <p className="text-body-sm text-ink-subtle mb-4">
              Used when scheduling a new appointment. You can still adjust it per-appointment.
            </p>
            <select
              name="defaultDuration"
              value={formData.defaultDuration}
              onChange={(e) => setFormData(prev => ({ ...prev, defaultDuration: Number(e.target.value) }))}
              className="input-field text-body-sm w-48"
            >
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
              <option value={150}>2.5 hours</option>
              <option value={180}>3 hours</option>
              <option value={210}>3.5 hours</option>
              <option value={240}>4 hours</option>
              <option value={270}>4.5 hours</option>
              <option value={300}>5 hours</option>
              <option value={330}>5.5 hours</option>
              <option value={360}>6 hours</option>
            </select>
          </div>

          <BookingPageCard
            profile={profile}
            slug={formData.slug}
            timezone={formData.timezone}
            availabilityDays={availabilityDays}
            blockedDates={blockedDates}
            newBlockedDate={newBlockedDate}
            newBlockedReason={newBlockedReason}
            onChange={handleChange}
            setAvailabilityDays={setAvailabilityDays}
            setNewBlockedDate={setNewBlockedDate}
            setNewBlockedReason={setNewBlockedReason}
            onAddBlockedDate={handleAddBlockedDate}
            onRemoveBlockedDate={handleRemoveBlockedDate}
            slugCheckStatus={slugCheck.status}
            slugSuggestion={slugCheck.suggestion}
            onSlugSuggestionAccept={handleSlugSuggestionAccept}
          />
          {saveFooter(handleSaveBooking)}
        </div>
      )}

      {/* Agreement tab */}
      {activeTab === 'agreement' && (
        <div className="space-y-6">
          <div className="card">
            <div className="flex items-start justify-between gap-4 mb-1">
              <h2 className="text-heading-md text-ink">Pre-Inspection Agreement</h2>
              <button
                type="button"
                onClick={() => setAgreementPreview((v) => !v)}
                className="text-body-sm text-ink-muted hover:text-ink underline-offset-2 hover:underline"
              >
                {agreementPreview ? 'Edit' : 'Preview'}
              </button>
            </div>
            <p className="text-body-sm text-ink-subtle mb-3">
              Your standard agreement template. When you confirm an appointment, it&rsquo;s sent to the client automatically for e-signature. Markdown is supported — use <code className="font-mono text-ink">**bold**</code>, <code className="font-mono text-ink">## headings</code>, and <code className="font-mono text-ink">- lists</code>.
            </p>
            <p className="text-body-xs text-ink-subtle mb-3">
              Available variables (replaced at send time):{' '}
              <code className="font-mono text-ink">{'{{client_name}}'}</code>,{' '}
              <code className="font-mono text-ink">{'{{property_address}}'}</code>,{' '}
              <code className="font-mono text-ink">{'{{inspection_date}}'}</code>,{' '}
              <code className="font-mono text-ink">{'{{inspector_name}}'}</code>.
            </p>
            {agreementPreview ? (
              <div className="border border-ink-subtle/20 rounded-lg p-4 min-h-[24rem] bg-white">
                {formData.agreementTemplate.trim() ? (
                  <div className="text-body-sm text-ink leading-relaxed">
                    <AgreementMarkdown>{formData.agreementTemplate}</AgreementMarkdown>
                  </div>
                ) : (
                  <p className="text-body-sm text-ink-subtle italic">Nothing to preview yet.</p>
                )}
              </div>
            ) : (
              <textarea
                name="agreementTemplate"
                value={formData.agreementTemplate}
                onChange={handleChange}
                rows={16}
                placeholder={'## Scope of inspection\n\nThis Pre-Inspection Agreement is between **{{inspector_name}}** and **{{client_name}}** for the property at {{property_address}}, scheduled for {{inspection_date}}.\n\n- Item one\n- Item two'}
                className="input-field font-mono text-body-sm"
              />
            )}
          </div>
          {saveFooter(handleSave)}
        </div>
      )}

      {/* Payments tab */}
      {activeTab === 'payments' && (
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-heading-md text-ink mb-1">Report delivery</h2>
            <p className="text-body-sm text-ink-subtle mb-4">
              Controls whether a report with a fee can be delivered to the client before payment is recorded. Leave on if you collect upfront; turn off if you invoice after delivery or bill through insurance.
            </p>
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                name="requirePaymentBeforeDelivery"
                checked={formData.requirePaymentBeforeDelivery}
                onChange={(e) => { setFormData((prev) => ({ ...prev, requirePaymentBeforeDelivery: e.target.checked })); setMessage(null); }}
                className="h-4 w-4 mt-0.5"
              />
              <span className="text-body-sm text-ink">
                Require payment before report delivery
                <span className="block text-body-xs text-ink-subtle mt-0.5">
                  When on, sending a report with a non-zero fee is blocked until you mark the payment received.
                </span>
              </span>
            </label>
          </div>

          <div className="card">
            <h2 className="text-heading-md text-ink mb-1">Payments</h2>
            <p className="text-body-sm text-ink-subtle mb-4">
              Drop in the payment link you already use (Square, Stripe Payment Link, QuickBooks invoice, PayPal.me, etc.). The report-delivery email shows a &ldquo;Pay your inspector&rdquo; button when you set a fee at send time.
            </p>
            <label className="block text-body-sm font-medium text-ink-muted mb-1">Payment link</label>
            <input
              type="url"
              name="paymentMethodUrl"
              value={formData.paymentMethodUrl}
              onChange={handleChange}
              placeholder="https://squareup.com/pay/..."
              maxLength={500}
              className="input-field"
            />
            <p className="text-body-xs text-ink-subtle mt-1 mb-4">Must be https://. Leave blank to hide the button.</p>

            <label className="block text-body-sm font-medium text-ink-muted mb-1">Button label</label>
            <input
              type="text"
              name="paymentMethodLabel"
              value={formData.paymentMethodLabel}
              onChange={handleChange}
              placeholder="Pay via Square"
              maxLength={50}
              className="input-field"
            />
            <p className="text-body-xs text-ink-subtle mt-1 mb-4">Falls back to &ldquo;Pay your inspector&rdquo; when blank.</p>

            <label className="block text-body-sm font-medium text-ink-muted mb-1">Other instructions</label>
            <textarea
              name="paymentInstructions"
              value={formData.paymentInstructions}
              onChange={handleChange}
              rows={5}
              maxLength={2000}
              placeholder={'Venmo: @acme-inspections\nZelle: pay@acmeinspections.com\nOr mail a check to: PO Box 123, Anytown, OR 97000'}
              className="input-field font-mono text-body-sm"
            />
            <p className="text-body-xs text-ink-subtle mt-1">Plain text only — shown under the button in the report-delivery email.</p>
          </div>

          {saveFooter(handleSave)}
        </div>
      )}

      {/* Communications tab */}
      {activeTab === 'communications' && (
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-heading-md text-ink mb-1">Review Requests</h2>
            <p className="text-body-sm text-ink-subtle mb-4">
              About a month after you publish a report, we&rsquo;ll email the client a friendly note asking them to leave a review at the link below. Skipped automatically if no link is set or the toggle is off.
            </p>
            <label className="block text-body-sm font-medium text-ink-muted mb-1">Review URL</label>
            <input type="url" name="reviewLink" value={formData.reviewLink} onChange={handleChange} placeholder="https://g.page/r/your-business/review" className="input-field" />
            <p className="text-body-xs text-ink-subtle mt-1 mb-4">Your Google Reviews link, Yelp page, or any other URL where clients can leave a review.</p>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                name="reviewRequestEnabled"
                checked={formData.reviewRequestEnabled}
                onChange={(e) => { setFormData((prev) => ({ ...prev, reviewRequestEnabled: e.target.checked })); setMessage(null); }}
                className="h-4 w-4"
              />
              <span className="text-body-sm text-ink">Send review-request emails 30 days after each published report</span>
            </label>
          </div>

          <div className="card">
            <h2 className="text-heading-md text-ink mb-1">Report Signature</h2>
            <p className="text-body-sm text-ink-subtle mb-4">
              Appears at the end of every report, after the disclaimer. Markdown (**bold**, *italic*) supported.
            </p>
            <textarea
              name="signatureText"
              value={formData.signatureText}
              onChange={handleChange}
              rows={5}
              placeholder={'Thank you for choosing us for your inspection.\n\nFor questions about this report, contact us at **(555) 123-4567** or hello@example.com.'}
              className="input-field font-mono text-body-sm"
            />
          </div>

          {saveFooter(handleSave)}
        </div>
      )}

      {/* Account tab */}
      {activeTab === 'account' && (
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-heading-md text-ink mb-4">Account</h2>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-body-sm text-ink-subtle">Email</div>
                <div className="text-body-sm text-ink font-medium">
                  {clerkUser?.primaryEmailAddress?.emailAddress ?? profile?.email ?? '—'}
                </div>
              </div>
              <button
                onClick={() => signOut()}
                className="px-4 py-2 text-body-sm font-medium text-sev-major border border-sev-major/30 rounded-md hover:bg-sev-major-soft transition-colors"
              >
                Sign out
              </button>
            </div>
          </div>

          <div className="card">
            <h2 className="text-heading-md text-ink mb-1">Send feedback</h2>
            <p className="text-body-sm text-ink-subtle mb-4">
              Drop anything that surprises you, breaks, or feels clunky. Opens your mail app — replies go straight to the team.
            </p>
            <a
              href="mailto:beta@flowspect.app?subject=Flowspect%20feedback"
              className="btn-secondary inline-block"
            >
              Email beta@flowspect.app
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
